/**
 * Global Error Handlers
 * Sets up window-level error handlers to catch unhandled errors
 */

import { 
  logError, 
  logResourceError, 
  logStorageError,
  logRouteError 
} from '../services/errorLogger';

/**
 * Handle window.onerror events
 */
const handleWindowError = (event) => {
  // Skip if this is an error from the error logger itself
  const errorMessage = event.message || '';
  if (errorMessage.includes('Error in error logger') || 
      errorMessage.includes('Failed to log error') ||
      errorMessage.includes('Supabase unavailable') ||
      errorMessage.includes('__ERROR_LOGGER__')) {
    return false;
  }

  const error = {
    message: event.message || 'Unknown error',
    filename: event.filename || '',
    lineno: event.lineno || 0,
    colno: event.colno || 0,
    error: event.error,
  };

  // Create a more detailed error message
  const detailedErrorMessage = `${error.message} (${error.filename}:${error.lineno}:${error.colno})`;
  const errorObj = event.error || new Error(detailedErrorMessage);
  errorObj.stack = errorObj.stack || `at ${error.filename}:${error.lineno}:${error.colno}`;

  // Log asynchronously and don't await to avoid blocking
  logError(errorObj, {
    errorType: 'window_error',
    severity: 'error',
  }).catch(() => {
    // Silently fail to avoid infinite loops
  });

  // Return false to allow default error handling
  return false;
};

/**
 * Handle unhandled promise rejections
 */
const handleUnhandledRejection = (event) => {
  const error = event.reason || new Error('Unhandled promise rejection');
  
  // Skip if this is an error from the error logger itself
  const errorMessage = error instanceof Error ? error.message : String(error);
  if (errorMessage.includes('Error in error logger') || 
      errorMessage.includes('Failed to log error') ||
      errorMessage.includes('Supabase unavailable') ||
      errorMessage.includes('__ERROR_LOGGER__')) {
    return;
  }
  
  // If it's not an Error object, convert it
  const errorObj = error instanceof Error 
    ? error 
    : new Error(String(error));

  // Log asynchronously and don't await to avoid blocking
  logError(errorObj, {
    errorType: 'promise_rejection',
    severity: 'critical', // Unhandled rejections are critical
  }).catch(() => {
    // Silently fail to avoid infinite loops
  });
};

/**
 * Handle resource loading errors (images, scripts, stylesheets, etc.)
 */
const handleResourceError = (event) => {
  const target = event.target;
  const resourceType = target.tagName?.toLowerCase() || 'unknown';
  const resourceUrl = target.src || target.href || 'unknown';
  
  // Don't log errors for placeholder images or data URIs
  if (resourceUrl.includes('data:') || resourceUrl.includes('placeholder')) {
    return;
  }

  logResourceError(resourceUrl, resourceType);
};

/**
 * Intercept console.error calls
 */
const interceptConsoleError = () => {
  const originalConsoleError = console.error;
  let isIntercepting = false;
  
  console.error = (...args) => {
    // Call original console.error first
    originalConsoleError.apply(console, args);
    
    // Prevent recursive interception
    if (isIntercepting) {
      return;
    }

    // Skip if this is an error from the error logger itself
    const firstArg = args[0];
    const errorMessage = typeof firstArg === 'string' ? firstArg : 
                        firstArg?.message || String(firstArg);
    
    if (errorMessage.includes('Error in error logger') || 
        errorMessage.includes('Failed to log error') ||
        errorMessage.includes('Supabase unavailable') ||
        errorMessage.includes('__ERROR_LOGGER__') ||
        errorMessage.includes('Error logging to Supabase')) {
      return;
    }
    
    isIntercepting = true;
    
    try {
      const errorMessage = args.map(arg => {
        if (arg instanceof Error) {
          return arg.message;
        }
        return String(arg);
      }).join(' ');
      
      const error = args.find(arg => arg instanceof Error) || new Error(errorMessage);
      
      // Log asynchronously and don't await to avoid blocking
      logError(error, {
        errorType: 'console_error',
        severity: 'warning',
      }).catch(() => {
        // Silently fail to avoid infinite loops
      });
    } catch (e) {
      // Silently fail to avoid breaking console.error
    } finally {
      isIntercepting = false;
    }
  };
};

/**
 * Wrap storage operations to catch errors
 */
const wrapStorageOperations = () => {
  // Wrap localStorage
  const originalLocalStorage = {
    getItem: localStorage.getItem.bind(localStorage),
    setItem: localStorage.setItem.bind(localStorage),
    removeItem: localStorage.removeItem.bind(localStorage),
    clear: localStorage.clear.bind(localStorage),
  };

  localStorage.getItem = function(key) {
    try {
      return originalLocalStorage.getItem(key);
    } catch (error) {
      logStorageError(error, 'getItem', key).catch(() => {});
      throw error;
    }
  };

  localStorage.setItem = function(key, value) {
    try {
      return originalLocalStorage.setItem(key, value);
    } catch (error) {
      logStorageError(error, 'setItem', key).catch(() => {});
      throw error;
    }
  };

  localStorage.removeItem = function(key) {
    try {
      return originalLocalStorage.removeItem(key);
    } catch (error) {
      logStorageError(error, 'removeItem', key).catch(() => {});
      throw error;
    }
  };

  localStorage.clear = function() {
    try {
      return originalLocalStorage.clear();
    } catch (error) {
      logStorageError(error, 'clear').catch(() => {});
      throw error;
    }
  };

  // Wrap sessionStorage similarly
  const originalSessionStorage = {
    getItem: sessionStorage.getItem.bind(sessionStorage),
    setItem: sessionStorage.setItem.bind(sessionStorage),
    removeItem: sessionStorage.removeItem.bind(sessionStorage),
    clear: sessionStorage.clear.bind(sessionStorage),
  };

  sessionStorage.getItem = function(key) {
    try {
      return originalSessionStorage.getItem(key);
    } catch (error) {
      logStorageError(error, 'getItem', key).catch(() => {});
      throw error;
    }
  };

  sessionStorage.setItem = function(key, value) {
    try {
      return originalSessionStorage.setItem(key, value);
    } catch (error) {
      logStorageError(error, 'setItem', key).catch(() => {});
      throw error;
    }
  };

  sessionStorage.removeItem = function(key) {
    try {
      return originalSessionStorage.removeItem(key);
    } catch (error) {
      logStorageError(error, 'removeItem', key).catch(() => {});
      throw error;
    }
  };

  sessionStorage.clear = function() {
    try {
      return originalSessionStorage.clear();
    } catch (error) {
      logStorageError(error, 'clear').catch(() => {});
      throw error;
    }
  };
};

/**
 * Catch image loading errors globally
 */
const setupImageErrorHandling = () => {
  // Handle existing images
  document.addEventListener('error', handleResourceError, true);
  
  // Also handle load errors for images
  document.addEventListener('error', (event) => {
    if (event.target && (event.target.tagName === 'IMG' || event.target.tagName === 'SCRIPT' || event.target.tagName === 'LINK')) {
      handleResourceError(event);
    }
  }, true);
};

/**
 * Wrap fetch to catch all network errors
 */
const wrapFetch = () => {
  const originalFetch = window.fetch;
  
  window.fetch = async function(...args) {
    try {
      const response = await originalFetch.apply(this, args);
      
      // Log failed requests (4xx, 5xx)
      // Skip logging errors from Supabase error_logs table to avoid loops
      const url = args[0]?.toString() || 'unknown';
      if (!response.ok && response.status >= 400 && !url.includes('error_logs')) {
        const error = new Error(`Fetch failed: ${response.status} ${response.statusText}`);
        error.status = response.status;
        
        logError(error, {
          errorType: 'network',
          severity: response.status >= 500 ? 'critical' : 'error',
          apiEndpoint: url,
          apiStatus: response.status,
        }).catch(() => {});
      }
      
      return response;
    } catch (error) {
      // Log network errors
      // Skip logging errors from Supabase error_logs table to avoid loops
      const url = args[0]?.toString() || 'unknown';
      if (!url.includes('error_logs')) {
        logError(error, {
          errorType: 'network',
          severity: 'critical',
          apiEndpoint: url,
        }).catch(() => {});
      }
      
      throw error;
    }
  };
};

/**
 * Setup all global error handlers
 */
export const setupGlobalErrorHandlers = () => {
  // Only set up handlers once
  if (window.__errorHandlersSetup) {
    return;
  }

  // Handle window.onerror
  window.addEventListener('error', handleWindowError);

  // Handle unhandled promise rejections
  window.addEventListener('unhandledrejection', handleUnhandledRejection);

  // Handle resource loading errors
  setupImageErrorHandling();

  // Intercept console.error
  interceptConsoleError();

  // Wrap storage operations
  wrapStorageOperations();

  // Wrap fetch for network error tracking
  wrapFetch();

  // Mark as setup
  window.__errorHandlersSetup = true;

  if (process.env.NODE_ENV === 'development') {
    console.log('✅ Global error handlers initialized');
  }
};

/**
 * Remove global error handlers (for testing or cleanup)
 */
export const removeGlobalErrorHandlers = () => {
  window.removeEventListener('error', handleWindowError);
  window.removeEventListener('unhandledrejection', handleUnhandledRejection);
  window.__errorHandlersSetup = false;
};

export default {
  setupGlobalErrorHandlers,
  removeGlobalErrorHandlers,
};
